import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import IconButton from "./IconButton";
import SystemIcon from "./SystemIcon";

const ACTION_MENU_WIDTH = 160;
const ACTION_MENU_EDGE = 8;
const ACTION_MENU_OFFSET = 4;

function joinClassNames(...names) {
  return names.filter(Boolean).join(" ");
}

function toCssLength(value) {
  if (value == null || value === "") return undefined;
  return typeof value === "number" ? `${value}px` : value;
}

export function DataTable({
  ariaLabel,
  className = "",
  density = "compact",
  minWidth = 720,
  style,
  children,
  ...props
}) {
  return (
    <div
      className={joinClassNames(
        "ds-data-table-scroll",
        density === "default" ? "ds-density--default" : "ds-density--compact",
      )}
    >
      <table
        className={joinClassNames("ds-data-table", className)}
        aria-label={ariaLabel}
        style={{
          ...style,
          "--ds-data-table-min-width": toCssLength(minWidth),
        }}
        {...props}
      >
        {children}
      </table>
    </div>
  );
}

export function DataTablePrimary({ children, secondary, className = "" }) {
  return (
    <div className={joinClassNames("ds-data-table__primary", className)}>
      <strong>{children == null || children === "" ? "-" : children}</strong>
      {secondary != null && secondary !== "" ? <span>{secondary}</span> : null}
    </div>
  );
}

export function DataTableActions({ className = "", children }) {
  return (
    <div className={joinClassNames("ds-data-table__actions", className)}>
      {children}
    </div>
  );
}

export function DataTableActionMenu({
  label = "더보기",
  actions = [],
  disabled = false,
  className = "",
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const focusLastRef = useRef(false);
  const menuId = useId();
  const triggerId = useId();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const visibleActions = actions.filter(Boolean);
  const menuDisabled = disabled || !visibleActions.some((action) => !action.disabled);
  const menuPositioned = position !== null;

  useEffect(() => {
    if (menuDisabled) setOpen(false);
  }, [menuDisabled]);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const triggerRect = trigger.getBoundingClientRect();
      const menuWidth = menu.offsetWidth || ACTION_MENU_WIDTH;
      const menuHeight = menu.offsetHeight || visibleActions.length * 32 + 8;
      const maxLeft = Math.max(ACTION_MENU_EDGE, window.innerWidth - menuWidth - ACTION_MENU_EDGE);
      const left = Math.max(ACTION_MENU_EDGE, Math.min(triggerRect.right - menuWidth, maxLeft));
      const below = triggerRect.bottom + ACTION_MENU_OFFSET;
      const above = triggerRect.top - menuHeight - ACTION_MENU_OFFSET;
      const top = below + menuHeight <= window.innerHeight - ACTION_MENU_EDGE
        ? below
        : Math.max(ACTION_MENU_EDGE, above);

      setPosition({ left, top });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, visibleActions.length]);

  useEffect(() => {
    if (!open || !menuPositioned) return undefined;

    const items = Array.from(
      menuRef.current?.querySelectorAll('[role="menuitem"]:not(:disabled)') || [],
    );
    const initialItem = focusLastRef.current ? items[items.length - 1] : items[0];
    (initialItem || menuRef.current)?.focus();
    return undefined;
  }, [menuPositioned, open]);

  useEffect(() => {
    if (!open) return undefined;

    const menuItems = () => Array.from(
      menuRef.current?.querySelectorAll('[role="menuitem"]:not(:disabled)') || [],
    );

    const closeOutside = (event) => {
      if (
        !triggerRef.current?.contains(event.target)
        && !menuRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key === "Tab") {
        setOpen(false);
        return;
      }
      if (!menuRef.current?.contains(document.activeElement)) return;

      const focusable = menuItems();
      if (!focusable.length) return;
      const currentIndex = focusable.indexOf(document.activeElement);
      let nextIndex;
      if (event.key === "ArrowDown") nextIndex = (currentIndex + 1) % focusable.length;
      else if (event.key === "ArrowUp") {
        nextIndex = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
      } else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = focusable.length - 1;
      else return;

      event.preventDefault();
      focusable[nextIndex].focus();
    };

    window.addEventListener("mousedown", closeOutside);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("mousedown", closeOutside);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const toggleMenu = (focusLast = false) => {
    if (menuDisabled) return;
    focusLastRef.current = focusLast;
    setPosition(null);
    setOpen((current) => !current);
  };

  const menu = open ? createPortal(
    <div
      ref={menuRef}
      id={menuId}
      className="ds-data-table__action-menu"
      role="menu"
      aria-labelledby={triggerId}
      tabIndex={-1}
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
      }}
    >
      {visibleActions.map((action, index) => (
        <button
          key={action.key ?? action.label ?? index}
          type="button"
          className={joinClassNames(
            "ds-data-table__action-menu-item",
            action.tone === "danger" && "is-danger",
            action.className,
          )}
          role="menuitem"
          disabled={action.disabled}
          title={action.title}
          tabIndex={-1}
          onClick={(event) => {
            setOpen(false);
            action.onClick?.(event);
          }}
        >
          {action.icon ? <SystemIcon name={action.icon} size="xsmall" /> : null}
          <span>{action.label}</span>
        </button>
      ))}
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <IconButton
        ref={triggerRef}
        id={triggerId}
        className={joinClassNames(
          "ds-data-table__action-button",
          open && "is-open",
          className,
        )}
        label={label}
        size="small"
        type="button"
        disabled={menuDisabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => toggleMenu(false)}
        onKeyDown={(event) => {
          if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            event.stopPropagation();
            toggleMenu(event.key === "ArrowUp");
          }
        }}
      >
        <SystemIcon name="more" size="xsmall" />
      </IconButton>
      {menu}
    </>
  );
}

export function DataTableSort({
  active = false,
  direction = "asc",
  onClick,
  children,
}) {
  const nextDirection = active && direction === "asc" ? "내림차순" : "오름차순";

  return (
    <button
      type="button"
      className={joinClassNames("ds-data-table__sort", active && "is-active")}
      onClick={onClick}
      aria-label={`${children} ${nextDirection} 정렬`}
    >
      <span>{children}</span>
      <span className="ds-data-table__sort-icon" aria-hidden="true">
        <SystemIcon
          name={active ? (direction === "desc" ? "sort-down" : "sort-up") : "sort-both"}
          size="xsmall"
        />
      </span>
    </button>
  );
}

export function DataTableState({
  colSpan,
  loading = false,
  empty = false,
  loadingText = "불러오는 중...",
  emptyText = "조회 결과가 없습니다.",
}) {
  if (!loading && !empty) return null;

  return (
    <tr>
      <td className="ds-data-table__state" colSpan={colSpan}>
        <span role={loading ? "status" : undefined}>{loading ? loadingText : emptyText}</span>
      </td>
    </tr>
  );
}
